import { Header } from "@tanstack/react-table";
import { useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { SHTableCell } from "@components/design-systems";
import { MSTableStickyConstants } from "@pages/managed-accounts/model-screener/constant";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { ModelScreenerRow } from "@models/model-screener/entities/modelScreenerData";
import { MSTableHeaderPopup } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-head-cell/popup";
import { useWindowZoomLevel } from "@hooks/useWindowZoomLevel";

type MSTableHeadCellProps = {
  header: Header<ModelScreenerRow, unknown>;
  isMainHeader: boolean;
};

export const MSTableHeadCell = ({
  header,
  isMainHeader,
}: MSTableHeadCellProps): JSX.Element => {
  const { palette } = useTheme();
  const windowZoomLevel = useWindowZoomLevel();
  const { scrolledLeft, scrolledTop } = useSelector(
    (state: RootState) => state.modelScreener.tableScrollProps,
  );

  // Refs and States
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { column, index } = header;
  const isCanSort = column.getCanSort();

  const isStickyCell = useMemo(
    () =>
      (!isMainHeader && index <= MSTableStickyConstants.LastBodyColumnIndex) ||
      (isMainHeader &&
        index <= MSTableStickyConstants.LastMainHeaderColumnIndex),
    [isMainHeader, index],
  );

  const inSpecialZoomLevel = useMemo(
    () => !isMainHeader && windowZoomLevel === 1.1,
    [isMainHeader, windowZoomLevel],
  );

  // Styles
  const isTriggeredWhiteBackground: boolean = useMemo(() => {
    if (isStickyCell) return !scrolledTop || !scrolledLeft;
    return !scrolledTop;
  }, [scrolledLeft, scrolledTop, isStickyCell]);

  // Data
  const metaData = column.columnDef.meta;

  return (
    <>
      <SHTableCell
        id={header.id}
        ref={anchorRef}
        colSpan={header.colSpan}
        sx={{
          display: "flex",
          zIndex: isStickyCell ? 1 : undefined,
          position: isStickyCell ? "sticky" : undefined,
          left: isStickyCell ? header.getStart() : undefined,
          background: isTriggeredWhiteBackground
            ? palette.secondary["50"]
            : undefined,
          p: header.index === 1 ? "0px !important" : "0px 10px !important",
          height: isMainHeader ? 25 : 42,
          width: header.getSize(),
          cursor: isCanSort ? "pointer" : "default",
          border: "none",
          borderRight: metaData?.isLastColumn
            ? `1px solid ${palette.divider}`
            : "0px solid transparent",
          borderTop: inSpecialZoomLevel
            ? `1px solid ${palette.grey["300"]} !important`
            : "0px solid transparent !important",
          marginTop: inSpecialZoomLevel ? "-1px !important" : "0px !important",
          borderBottomWidth: isMainHeader ? "0px !important" : undefined,
        }}
      >
        <MSTableHeaderPopup
          anchorEl={anchorRef.current}
          header={header}
          isMainHeader={isMainHeader}
        />
      </SHTableCell>
    </>
  );
};
